import { Table, Tag, Modal, Button } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import ChangeFieldView, {
  getChangeFieldRows,
} from "@/components/views/ChangeFieldView/ChangeFieldView";
import CollapsibleList from "@/components/ui/CollapsibleList";

const changeTypeMap = {
  created: { label: "Создание", color: "green", icon: <PlusOutlined /> },
  updated: { label: "Изменение", color: "default", icon: <EditOutlined /> },
  deleted: { label: "Удаление", color: "red", icon: <DeleteOutlined /> },
};

const taskFieldsLabels = {
  task_number: "№",
  name: "Название",
  description: "Описание",
  start_date: "Дата начала",
  end_date: "Дата окончания",
  due_date: "Срок исполнения",
  is_accessible_for_all_in_unit: "Уведомлять всех в рабочей группе",
  location: "Местоположение",
  author: "Автор",
  executor: "Исполнитель",
  assignee_acceptor: "Приемщик",
  recipient: "Получатель",
  parent: "Родительская задача",
  unit: "Рабочая группа",
  folder: "Папка",
  importance: "Приоритет",
  status: "Статус",
  files: "Файлы",
  subTasks: "Подзадачи",
  relatedObjects: "Прикрепленные объекты",
  coexecutors: "Соисполнители",
  firstname: "Имя",
  lastname: "Фамилия",
  patronymic: "Отчество",
  definition: "Описатель",
  type: "Тип",
  object: "Объект",
  repository: "Справочник",
  checklist: "Чек-лист",
  idx: "Порядок ",
  is_completed: "Выполнено",
  title: "Название задачи",
  template_title: "Название шаблона задачи",
  template_number: "Номер шаблона задачи",
  is_visible: "Видимость",
  planned_costs: "Планируемые трудозатраты",
  actual_costs: "Фактические трудозатраты",
  timeEntries: "Трудозатраты",
  hours: "Часы",
  employee: "Сотрудник",
  recorded_at: "Дата исполнения",
  require_actuals_on_acceptance:
    "Обязательное заполнение фактических трудозатрат",
  is_recurring_template: "Периодический шаблон",
  quantity: "Количество",
  use_permission_rules: "Расширенные права",
  measure_unit: "Единица измерения",
  nested_resources: "Связанные ресурсы",
  field_name: "Название поля",
  do_not_exceed_children: "Не превышать трудозатраты подзадач",
  duration: "Длительность",
  sprint: "Спринт",
  sprint_uuid: "Спринт",
  comments: "Комментарии",
  body: "Текст комментария",
  quoted_comment: "Цитируемый комментарий",
  reactions: "Реакции",
  emoji: "Эмодзи",
  count: "Количество",
  users: "Пользователи",
  roles: "Роли",
  avatar: "Аватар",
};

const taskValueLabels = {
  object: "Объект",
  repository: "Справочник",
  "1_week": "Неделя",
  "2_week": "2 недели",
  "3_week": "3 недели",
  "4_week": "4 недели",
  other: "Другое",
};

const taskLogIgnoreKeys = [
  "created_at",
  "updated_at",
  "meta",
  "uuid",
  "status_uuid",
  "importance_uuid",
  "author_uuid",
  "responsible_executor_uuid",
  "recipient_uuid",
  "parent_task_uuid",
  "unit_uuid",
  "folder_uuid",
  "email",
  "is_notification_sent",
  "deleted",
  "is_personal",
  "unit_type",
  "table_name",
  "closed_at",
  "quoted_comment_deleted",
  "is_edited",
  "reacted_by_me",
  "removed_from_unit",
];

const ChangedFieldsList = ({ record }) => {
  const items = getChangeFieldRows({
    before: record.before_change,
    after: record.after_change,
    onlyChanged: true,
    fieldLabels: taskFieldsLabels,
    ignoreKeys: taskLogIgnoreKeys,
  }).map((row) => row.displayName);

  return items.length ? (
    <CollapsibleList items={items} count={2} width={220} wrap />
  ) : (
    "—"
  );
};

const LogsTable = ({ data, pagination, loading, onChange }) => {
  const [preview, setPreview] = useState(null);
  const openPreview = (record) => setPreview(record);
  const closePreview = () => setPreview(null);

  const columns = useMemo(
    () => [
      {
        title: "",
        dataIndex: "change_type",
        key: "change_type",
        width: 48,
        render: (change_type) => {
          const cfg = changeTypeMap[change_type] || {
            label: change_type,
            color: "default",
          };
          return (
            <Tag
              color={cfg.color}
              icon={cfg.icon}
              title={cfg.label}
              aria-label={cfg.label}
            />
          );
        },
      },
      {
        title: "Автор изменения",
        dataIndex: "actor",
        width: 250,
        key: "actor",
        render: (employee) =>
          employee ? <EmployeeProfile {...employee} /> : "—",
        ellipsis: { showTitle: true },
      },
      {
        title: "Измененные поля",
        key: "changed_fields",
        width: 250,
        render: (_, record) => <ChangedFieldsList record={record} />,
      },
      {
        title: "Дата/время изменения",
        dataIndex: "change_date",
        key: "change_date",
        width: 200,
        render: (val) => (val ? dayjs(val).format("DD.MM.YYYY HH:mm") : "—"),
      },
      {
        title: "",
        width: 80,
        align: "center",
        fixed: "right",
        key: "actions",
        render: (_, record) => (
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => openPreview(record)}
          ></Button>
        ),
      },
    ],
    [],
  );

  const renderChangesFieldsModal = () => {
    if (!preview) return null;

    return (
      <Modal
        open={!!preview}
        onCancel={closePreview}
        onOk={closePreview}
        okText="Закрыть"
        cancelButtonProps={{ style: { display: "none" } }}
        title="Изменения задачи"
        width={1000}
      >
        <ChangeFieldView
          before={preview.before_change}
          after={preview.after_change}
          changeType={preview.change_type}
          onlyChanged
          fieldLabels={taskFieldsLabels}
          valueLabels={taskValueLabels}
          ignoreKeys={taskLogIgnoreKeys}
        />
      </Modal>
    );
  };

  return (
    <>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="uuid"
        loading={loading}
        pagination={pagination}
        onChange={onChange}
        showSorterTooltip={false}
        scroll={{ x: "100%" }}
        rowClassName="top-align"
      />
      {renderChangesFieldsModal()}
    </>
  );
};

export default LogsTable;
